import { motion } from 'framer-motion';
import {
  Target,
  TrendingUp,
  Users,
  BarChart3,
  ArrowRight,
  Map,
  Lightbulb,
  CheckCircle,
  AlertCircle,
  DollarSign,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function MarketAnalysisPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'External Assessment', path: '/external-assessment' },
              { label: 'Market Analysis' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Map className="w-4 h-4 mr-2" />
            External Assessment Tool 3 of 4
          </span>
        }
        title="Find the market you can realistically win"
        subtitle="Replace a broad market estimate with a focused opportunity. Understand who buys, why they buy, and which customer segments your business is equipped to serve."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Explore your market
        </Link>
      </PageHero>

      {/* TAM/SAM/SOM Framework */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              Size the opportunity you can actually pursue
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A large total market does not make every customer reachable. Separate TAM, SAM, and
              SOM to make your assumptions, constraints, and priorities explicit.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card bg-white p-8 rounded-xl border border-gray-200"
            >
              <Target className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-2xl font-bold mb-3">TAM</h3>
              <p className="text-gray-600 mb-2 font-semibold">Total Addressable Market</p>
              <p className="text-sm text-gray-700 mb-4">
                The total market demand for your product category if you achieved 100% market share
                globally with no constraints.
              </p>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-900">How to Calculate:</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Top-down: Industry reports, analyst estimates
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Bottom-up: Total potential users × average revenue
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Value theory: Problem frequency × willingness to pay
                  </li>
                </ul>
              </div>
            </motion.div>
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="public-card bg-white p-8 rounded-xl border border-gray-200"
            >
              <Users className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold mb-3">SAM</h3>
              <p className="text-gray-600 mb-2 font-semibold">Serviceable Available Market</p>
              <p className="text-sm text-gray-700 mb-4">
                The segment of TAM you can realistically serve with your current business model,
                products, and geographic reach.
              </p>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-900">Constraints to Consider:</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Geographic limitations (where you operate)
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Product/service scope (what you offer)
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Channel access (how you reach customers)
                  </li>
                </ul>
              </div>
            </motion.div>
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="public-card bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-200"
            >
              <TrendingUp className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-2xl font-bold mb-3">SOM</h3>
              <p className="text-gray-600 mb-2 font-semibold">Serviceable Obtainable Market</p>
              <p className="text-sm text-gray-700 mb-4">
                The portion of SAM you can realistically capture in the near term (1-3 years) given
                current resources and competition.
              </p>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-900">Reality Factors:</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Competitive intensity (market share you can win)
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Sales/marketing capacity (your reach)
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                    Financial resources (growth speed limits)
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          {/* Example Calculation */}
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="public-card max-w-4xl mx-auto bg-white p-8 rounded-xl border border-gray-200"
          >
            <h3 className="text-2xl font-bold mb-6 flex items-center">
              <BarChart3 className="w-6 h-6 mr-3 text-blue-600" />
              Example: B2B Project Management SaaS
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <span className="font-semibold text-gray-900">
                    TAM (Total Addressable Market)
                  </span>
                  <span className="text-2xl font-bold text-blue-600">$45B</span>
                </div>
                <p className="text-sm text-gray-600">
                  Global project management software market × all businesses needing PM tools = 50M
                  companies × $900 average annual spend
                </p>
              </div>
              <div className="flex items-center justify-center text-gray-400">
                <ArrowRight className="w-5 h-5 rotate-90" />
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <span className="font-semibold text-gray-900">
                    SAM (Serviceable Available Market)
                  </span>
                  <span className="text-2xl font-bold text-purple-600">$4.5B</span>
                </div>
                <p className="text-sm text-gray-600">
                  English-speaking markets + 50-500 employee segment + cloud-based focus = 5M
                  companies × $900
                </p>
              </div>
              <div className="flex items-center justify-center text-gray-400">
                <ArrowRight className="w-5 h-5 rotate-90" />
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <span className="font-semibold text-gray-900">
                    SOM (Serviceable Obtainable Market)
                  </span>
                  <span className="text-2xl font-bold text-green-600">$90M</span>
                </div>
                <p className="text-sm text-gray-600">
                  Realistic 3-year target: 2% market share in SAM given current sales capacity and
                  $20M funding = 100K companies × $900
                </p>
              </div>
            </div>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-blue-700">Strategic Insight:</span> While TAM is
                large ($45B), the realistic near-term opportunity (SOM = $90M) requires disciplined
                execution. Focus resources on winning 2% of the serviceable market before expanding
                to adjacent segments.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Market Segmentation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Market Segmentation</h2>
              <p className="text-xl text-gray-600">
                Not all customers are created equal—segment strategically to focus on the most
                valuable opportunities
              </p>
            </motion.div>

            <div className="space-y-6 mb-12">
              {[
                {
                  method: 'Demographic Segmentation',
                  icon: Users,
                  description: 'Divide market by observable characteristics',
                  examples: [
                    'Company size (employees, revenue)',
                    'Industry vertical (healthcare, finance, retail)',
                    'Geographic location (region, country, urban/rural)',
                    'Company age (startup, growth, mature)',
                  ],
                  when: 'When these factors drive different needs, budgets, or buying processes',
                },
                {
                  method: 'Firmographic Segmentation (B2B)',
                  icon: Building2,
                  description: 'Business-specific attributes that affect purchasing',
                  examples: [
                    'Annual revenue and budget cycles',
                    'Organizational structure (centralized vs. decentralized)',
                    'Technology stack and maturity',
                    'Growth stage and funding status',
                  ],
                  when: 'For B2B sales where company characteristics predict fit and buying behavior',
                },
                {
                  method: 'Behavioral Segmentation',
                  icon: TrendingUp,
                  description: 'Group by actions, usage patterns, and engagement',
                  examples: [
                    'Product usage intensity (power users vs. casual)',
                    'Purchase frequency and loyalty',
                    'Feature adoption patterns',
                    'Customer journey stage (awareness, consideration, decision)',
                  ],
                  when: 'When current behavior predicts future value and expansion potential',
                },
                {
                  method: 'Needs-Based Segmentation',
                  icon: Target,
                  description: 'Segment by underlying problems and desired outcomes',
                  examples: [
                    'Job-to-be-done (what success looks like)',
                    'Pain point severity (nice-to-have vs. critical)',
                    'Outcome priorities (speed vs. quality vs. cost)',
                    'Risk tolerance and innovation appetite',
                  ],
                  when: 'When different customer groups need fundamentally different value propositions',
                },
                {
                  method: 'Value-Based Segmentation',
                  icon: DollarSign,
                  description: 'Prioritize by lifetime value and profit potential',
                  examples: [
                    'Customer lifetime value (CLV)',
                    'Acquisition cost vs. revenue potential',
                    'Expansion and upsell likelihood',
                    'Retention and churn risk',
                  ],
                  when: 'When allocating limited resources and determining which segments to target first',
                },
              ].map((seg, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 p-3 rounded-lg">
                      <seg.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{seg.method}</h3>
                      <p className="text-gray-700 mb-4">{seg.description}</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-sm text-gray-900 mb-2">Examples:</h4>
                          <ul className="space-y-1">
                            {seg.examples.map((ex, i) => (
                              <li key={i} className="text-xs text-gray-600 flex items-start">
                                <CheckCircle className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" />
                                {ex}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h4 className="font-semibold text-sm text-gray-900 mb-1">Use When:</h4>
                          <p className="text-xs text-gray-700">{seg.when}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card bg-blue-50 p-6 rounded-xl border border-gray-200"
            >
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-blue-600" />
                Pro Tip: Combine Multiple Segmentation Methods
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The most effective segmentation uses multiple dimensions. For example: "Mid-market
                healthcare companies (demographic) with high regulatory compliance needs
                (needs-based) who are early technology adopters (behavioral)." Multi-dimensional
                segments are more actionable because they define both who to target and how to
                message them.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Buyer Behavior & Decision Process */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Understanding the Buying Process</h2>
              <p className="text-xl text-gray-600">
                Map how customers make purchase decisions to optimize your sales and marketing
                strategy
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  stage: 'Problem Recognition',
                  description: 'Buyer realizes they have a need or problem worth solving',
                  questions: [
                    'What triggers awareness of the problem?',
                    'Is the problem urgent or can it wait?',
                    'Who first recognizes the problem?',
                  ],
                  tactics: [
                    'Thought leadership content',
                    'Problem-framing case studies',
                    'Industry benchmarks',
                  ],
                },
                {
                  stage: 'Information Search',
                  description: 'Buyer researches solutions and learns what options exist',
                  questions: [
                    'Where do they search (Google, peers, analysts)?',
                    'What criteria matter most?',
                    'Who influences their research?',
                  ],
                  tactics: [
                    'SEO and content marketing',
                    'Analyst relations',
                    'Review site optimization',
                  ],
                },
                {
                  stage: 'Evaluation of Alternatives',
                  description: 'Buyer compares vendors and narrows down options',
                  questions: [
                    'What differentiators matter most?',
                    'How do they compare alternatives?',
                    'What are deal-breakers?',
                  ],
                  tactics: ['Comparison guides', 'Product demos', 'Proof of concept trials'],
                },
                {
                  stage: 'Purchase Decision',
                  description: 'Buyer selects a vendor and negotiates terms',
                  questions: [
                    'Who has final approval authority?',
                    'What legal/procurement hurdles exist?',
                    'What causes last-minute changes?',
                  ],
                  tactics: [
                    'Executive access',
                    'Risk mitigation (guarantees, references)',
                    'Flexible contracting',
                  ],
                },
                {
                  stage: 'Post-Purchase Evaluation',
                  description: 'Buyer assesses whether purchase met expectations',
                  questions: [
                    'How quickly do they see value?',
                    'What drives satisfaction or regret?',
                    'Will they expand or churn?',
                  ],
                  tactics: [
                    'Onboarding excellence',
                    'Proactive success management',
                    'Usage monitoring and intervention',
                  ],
                },
              ].map((stage, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-center mb-4">
                    <div className="bg-purple-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mr-3">
                      {idx + 1}
                    </div>
                    <h3 className="text-xl font-bold">{stage.stage}</h3>
                  </div>
                  <p className="text-gray-700 mb-4 text-sm">{stage.description}</p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-xs text-gray-900 mb-2">Key Questions:</h4>
                      <ul className="space-y-1">
                        {stage.questions.map((q, i) => (
                          <li key={i} className="text-xs text-gray-600 flex items-start">
                            <span className="text-purple-500 mr-1.5">•</span>
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <h4 className="font-semibold text-xs text-gray-900 mb-1">
                        Sales/Marketing Tactics:
                      </h4>
                      <ul className="space-y-1">
                        {stage.tactics.map((t, i) => (
                          <li key={i} className="text-xs text-gray-700">
                            → {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}

              <motion.div
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="public-card md:col-span-2 bg-indigo-50 p-6 rounded-xl border border-gray-200"
              >
                <h3 className="font-semibold text-gray-900 mb-3">B2B Buying Committees</h3>
                <p className="text-gray-700 text-sm mb-4">
                  In B2B, multiple stakeholders influence decisions. Map the buying committee:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    {
                      role: 'Economic Buyer',
                      desc: 'Controls budget, final approval',
                      strategy: 'ROI, risk mitigation, strategic value',
                    },
                    {
                      role: 'Technical Evaluator',
                      desc: 'Assesses product fit, integration',
                      strategy: 'Product depth, tech specs, implementation',
                    },
                    {
                      role: 'User/Champion',
                      desc: 'Daily user, internal advocate',
                      strategy: 'Ease of use, workflow improvement, quick wins',
                    },
                  ].map((persona, i) => (
                    <div key={i} className="bg-white p-4 rounded-lg">
                      <h4 className="font-bold text-sm mb-1">{persona.role}</h4>
                      <p className="text-xs text-gray-600 mb-2">{persona.desc}</p>
                      <p className="text-xs text-indigo-700">
                        <span className="font-semibold">Messaging:</span> {persona.strategy}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Market Analysis Mistakes</h2>
              <p className="text-xl text-gray-600">
                Avoid these pitfalls when sizing and segmenting your market
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Inflating TAM with Unrealistic Assumptions',
                  description:
                    'Assuming everyone who could theoretically use your product will buy it. Investors see through optimistic TAM calculations immediately.',
                  fix: "Use conservative assumptions. Show your math. Compare to comparable markets. If your TAM seems implausibly large, you've defined too broadly.",
                },
                {
                  mistake: 'Ignoring Market Growth Rate',
                  description:
                    "Sizing today's market without considering growth trajectory. A $500M fast-growing market may be better than a $2B stagnant one.",
                  fix: 'Research historical growth rates and future projections. Factor market maturity (emerging, growth, mature, declining) into strategy.',
                },
                {
                  mistake: 'No Prioritization Among Segments',
                  description:
                    'Defining 10 segments but treating them all equally. This dilutes focus and wastes resources on low-potential opportunities.',
                  fix: 'Rank segments by attractiveness (size, growth, profitability) and fit (ease of acquisition, competitive strength). Pick 1-2 primary targets.',
                },
                {
                  mistake: 'Segmenting by What You Can Measure vs. What Matters',
                  description:
                    'Using easily available demographics when behavior or needs would be more predictive of success.',
                  fix: 'Start with strategic segmentation (what matters for fit and value). Then figure out how to identify/reach those segments.',
                },
                {
                  mistake: 'Not Validating with Real Customers',
                  description:
                    'Relying entirely on secondary research and assumptions without talking to actual buyers in your target segments.',
                  fix: 'Conduct 20-30 customer interviews. Ask about buying process, decision criteria, budget cycles, and pain points. Real voice trumps analyst reports.',
                },
                {
                  mistake: 'Static Market Analysis',
                  description:
                    'Creating market analysis once during fundraising or annual planning, then ignoring as markets evolve.',
                  fix: 'Review market size and segmentation quarterly. Track leading indicators (new entrants, customer feedback, competitive moves) that signal shifts.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Build a market view you can make decisions with
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Define the opportunity</h3>
                <p className="text-gray-600">
                  Estimate the total, serviceable, and obtainable market with clear sources and
                  assumptions.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Focus on the right segments</h3>
                <p className="text-gray-600">
                  Compare customer needs, buying behavior, and fit with your offer.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Choose where to start</h3>
                <p className="text-gray-600">
                  Use that evidence to prioritize the customers and channels your strategy will
                  pursue.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/external-assessment/competitive-positioning"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-blue-600">Competitive Positioning</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Position your offering uniquely within your target market segments
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/growth-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-purple-600">Growth Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Use market analysis insights to identify highest-potential growth opportunities
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/foundation/strategic-objectives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Strategic Objectives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Set quantifiable objectives based on market opportunity and realistic SOM
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/external-assessment/pestel-analysis"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: PESTEL Analysis
              </Link>
              <Link
                to="/external-assessment/competitive-positioning"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Competitive Positioning <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Explore your market in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Use that evidence to prioritize the customers and channels your strategy will pursue.
            Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Explore your market <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

// Missing import
import { Building2 } from 'lucide-react';
